import { createAsyncThunk } from "@reduxjs/toolkit"

import { sortedRecipesHelper } from "../../utils/recipeList/recipelistUtils"
import { RootState } from "../store/store"
import { User, UserSettings } from "../main/main.types"
import { DEFAULT_FILTERS } from "./search.constants"
import { setClearFilters, setSearchLocalFilterRecipes, setSearchQueryLocalFilteredRecipes, setSortedRecipes } from "./search.slice"
import { SearchFilteredRecipesInterface, SearchHit } from "./search.types"
import { dietSelectionStringHandler, filterIngredientsSource, filterRecipes, filterStringHandler, getServiceIngredientsResults, getServiceSearchResults, isLowerCaseIncludes } from "./search.utils"

export const getServiceQuerySearchList = createAsyncThunk(
  'search/getServiceQuerySearchList',
  async ({ queryString, filterString }: { queryString: string, filterString: string }) => {
    const serviceSearchResult = await getServiceSearchResults(queryString, filterString)
    return { algoliaResults: serviceSearchResult }
  }
)

export const getServiceQueryIngredientsList = createAsyncThunk(
  'search/getServiceQueryIngredientsList',
  async ({ queryString, filterString }: { queryString: string, filterString: string }) => {
    const serviceIngredientsResult = await getServiceIngredientsResults(queryString, filterString)
    return { algoliaResults: serviceIngredientsResult }
  }
)

export const getServiceFiltersSearchList = createAsyncThunk(
  'search/getServiceFiltersSearchList',
  async ({ filterString, filteredRecipes }: { filterString: string, filteredRecipes: Array<SearchFilteredRecipesInterface> }) => {
    const serviceSearchResult = await getServiceSearchResults('', filterString)
    return { algoliaResults: serviceSearchResult, filteredRecipes }
  }
)

export const duplicationFilterStringHandler = (user: User | undefined) => {
  const resultDietAndPreference: Array<string> = [];
  const resultDietPreferenceString: string = dietSelectionStringHandler(user?.userSettings?.diet, user?.id)
  const userAllergens = user?.userSettings?.allergens ?? []
  const allDietString = 'diet:Non-Veg OR diet:Veg OR diet:Vegan OR diet:Egg';
  const createdByString = `(created_by: ${user?.id} OR is_public: true)`
  if (userAllergens.length) {
    userAllergens.forEach((allergen: string) => {
      resultDietAndPreference.push(`NOT allergens:${allergen}`)
    })
  }

  const dietCondition = `(${resultDietPreferenceString || allDietString} OR NOT diet:*)`;

  resultDietAndPreference.push(dietCondition);

  const allergenAndPreference = resultDietAndPreference.join(" AND ")

  const resultDietAndPreferenceString = `${createdByString} AND ${allergenAndPreference}`;

  return resultDietAndPreferenceString;
}

export const getDuplicateRecipeSearchList = ({ queryString }: { queryString: string }) => async (dispatch: any, getState: any) => {
  const state: RootState = getState();
  const { user } = state.main
  const filterString = user?.id ? duplicationFilterStringHandler(user) : '';
  dispatch(getServiceQuerySearchList({ queryString, filterString }))
}

export const getQuerySearchList = ({ enableAlgoliaSearch, enableAdminFeatures, queryString }: { enableAlgoliaSearch: boolean, enableAdminFeatures: boolean, queryString: string }) => async (dispatch: any, getState: any) => {
  const state: RootState = getState();
  if (enableAlgoliaSearch) {
    const { user } = state.main
    const { searchFilters } = state.search

    const filterString = filterStringHandler(user, searchFilters, enableAdminFeatures)

    dispatch(getServiceQuerySearchList({ queryString, filterString }))
  } else {

    const { recipeList, experimentalRecipeList } = state.homepage;
    const allRecipes: Array<SearchHit> = [...recipeList, ...(enableAdminFeatures ? experimentalRecipeList : [])].map((recipe: SearchFilteredRecipesInterface) => ({
      id: recipe.id,
      metadata: {
        name: recipe.metadata.name,
        url: recipe.metadata.url
      }
    }));
    const localFilteredRecipes: Array<SearchHit> = allRecipes.filter((recipe: SearchHit) => isLowerCaseIncludes(recipe.metadata.name, queryString));
    dispatch(setSearchQueryLocalFilteredRecipes({ localFilteredRecipes, queryString }))
  }

}

export const getQueryIngredientsList = ({ enableAlgoliaSearch, queryString }: { enableAlgoliaSearch: boolean, queryString: string }) => async (dispatch: any, getState: any) => {
  const state: RootState = getState();
  if (enableAlgoliaSearch) {
    const { user } = state.main
    const filterString = filterIngredientsSource(user?.id ?? "");
    dispatch(getServiceQueryIngredientsList({ queryString, filterString }))
  }
}

export const applySearchFilters = ({ enableAlgoliaSearch, enableAdminFeatures }: { enableAlgoliaSearch: boolean, enableAdminFeatures: boolean }) => async (dispatch: any, getState: any) => {
  const state: RootState = getState();
  const { recipeMetaData, experimentalRecipeList } = state.homepage
  const { searchFilters } = state.search
  const userSettings = state.main.user?.userSettings ?? {}
  const filteredRecipes = filterRecipes(userSettings, recipeMetaData ?? [], searchFilters)
  if (enableAlgoliaSearch) {
    const { user } = state.main
    const filterString = filterStringHandler(user, searchFilters, enableAdminFeatures)
    dispatch(getServiceFiltersSearchList({ filterString, filteredRecipes }))
  } else {
    const localFilteredRecipes: Array<SearchFilteredRecipesInterface> = [...filteredRecipes ?? [], ...(enableAdminFeatures ? experimentalRecipeList : []) ?? []]
    dispatch(setSearchLocalFilterRecipes({ localFilteredRecipes, filteredRecipes }))
  }

};

export const clearSearchFilters = (enableAdminFeatures: boolean) => (dispatch: any, getState: any) => {
  const state: RootState = getState();
  const { recipeMetaData, experimentalRecipeList } = state.homepage
  const { defaultExplorePageSearchList } = state.search
  const userSettings = state.main.user?.userSettings ?? {}
  const filteredRecipes = filterRecipes(userSettings, recipeMetaData ?? [], DEFAULT_FILTERS)
  const localFilteredRecipes: Array<SearchFilteredRecipesInterface> = [...filteredRecipes ?? [], ...(enableAdminFeatures ? experimentalRecipeList : []) ?? []]
  dispatch(setClearFilters({ localFilteredRecipes: defaultExplorePageSearchList }))
}

export const sortRecipes = (sortMode: string, recipeOrdering: Array<any>) => (dispatch: any, getState: any) => {
  const state: RootState = getState();
  const filteredRecipes = state.search.searchFilteredRecipes
  const sortedRecipes = sortedRecipesHelper(sortMode, filteredRecipes, recipeOrdering)
  dispatch(setSortedRecipes({ sortedRecipes }))
}

